/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import type { Theme } from '@coursera/cds-core';
import { Button, Grid, Link, Typography2 } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';

import { humanizeTime } from 'bundles/author-course/utils/CourseMaterialUtils';
import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import type { TrackingProps } from 'bundles/page/components/TrackedLink2';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/degree-home';

import {
  DismissErrorNotification,
  DismissSuccessNotification,
  DismissWarningNotification,
} from './DismissNotifications';
import { RestoreButton } from './RestoreButton';
import { RestoreSuccessNotification } from './RestoreSuccessNotification';
import type { FilterOptionValuesType } from './constants/constants';

const styles: Record<string, Interpolation<Theme>> = {
  itemContainer: () => css`
    border-top: 1px solid var(--cds-color-grey-50);
    padding: 0.8rem 0 1rem;
  `,
  itemSubText: () => css`
    color: var(--cds-color-grey-700);
    font-size: 14px;
  `,
  itemDuration: () => css`
    color: var(--cds-color-grey-700);
    font-weight: normal;
    font-size: 14px;
  `,
  itemName: css`
    font-weight: normal;
  `,
  itemHeaderLine: css`
    display: inline;
  `,
  itemHeaderText: css`
    margin-right: 16px;
  `,
  itemPillContainer: css`
    display: inline-block;
  `,
  gradeWeight: css`
    display: inline-block;
  `,
  courseName: css`
    a {
      color: inherit !important;
    }
  `,
  itemIcon: css`
    padding-left: 0 !important;
    padding-right: 0 !important;
  `,
  dismissButton: css`
    padding: 4px;
    margin-bottom: auto;
    position: static;
    max-width: 35px;
    width: 100%;

    & .cds-button-endIcon {
      margin-left: 0;
    }

    &:focus,
    &:hover {
      background: var(--cds-color-blue-700);
    }

    &:focus svg,
    &:hover svg {
      color: var(--cds-color-neutral-primary-invert);
    }
  `,
};

export const CalendarListItem = ({
  itemIcon,
  itemTypeName,
  itemName,
  itemUrl,
  duration,
  statusPill,
  subHeading,
  trackingName,
  courseId,
  itemId,
  isDismissible,
  isRestorable,
  itemFilterType,
  hideColon,
  openLinkInNewTab,
  additionalTrackData = {},
  itemStatus,
}: {
  itemIcon: React.ReactElement;
  itemTypeName: string;
  itemName: string;
  itemUrl?: string;
  duration?: number;
  statusPill?: React.ReactElement | null;
  subHeading: React.ReactChild;
  trackingName: string;
  courseId: string;
  itemId: string;
  isDismissible?: boolean;
  isRestorable?: boolean;
  itemFilterType: FilterOptionValuesType;
  hideColon?: boolean;
  openLinkInNewTab?: boolean;
  additionalTrackData?: unknown;
  itemStatus?: string;
}) => {
  const { refetchCalendarItems, setRefetchRequired } = useDegreeContext();
  const [showWarningModal, setWarningModal] = useState(false);
  const [showDismissSuccessModal, setDismissSuccessModal] = useState(false);
  const [showErrorModal, setErrorModal] = useState(false);

  // Used to hide the item if it's in the process of being dismissed
  // i.e. the request hasn't been sent to the server, or we haven't received an updated calendarItemsList with the dismissal
  const [isDismissed, setIsDismissed] = useState(false);

  const [showRestoreSuccessModal, setRestoreSuccessModal] = useState(false);

  const showModal = showWarningModal || showDismissSuccessModal || showErrorModal;
  const headerContents = (
    <React.Fragment>
      {itemTypeName}
      {hideColon ? '' : ': '}
      <span css={styles.itemName}>{itemName}</span>
      {duration && <span css={styles.itemDuration}> {humanizeTime(duration)}</span>}
    </React.Fragment>
  );

  const hideOverdueItem = () => {
    setWarningModal(false);
    setDismissSuccessModal(true);
    setIsDismissed(true);
  };

  const hideOverdueItemError = () => {
    setWarningModal(false);
    setDismissSuccessModal(false);
    setErrorModal(true);
    setIsDismissed(false);
  };

  const dismissCalendarItemSuccess = () => {
    setDismissSuccessModal(false);
    refetchCalendarItems();
    setRefetchRequired(false);
  };

  const restoreCalendarItemSuccess = () => {
    setRestoreSuccessModal(false);
    refetchCalendarItems();
    setRefetchRequired(false);
  };

  return (
    <>
      {showWarningModal && (
        <DismissWarningNotification
          courseId={courseId}
          itemId={itemId}
          onHide={hideOverdueItem}
          onCancel={() => setWarningModal(false)}
          onError={hideOverdueItemError}
          itemFilterType={itemFilterType}
        />
      )}
      {showDismissSuccessModal && (
        <DismissSuccessNotification onDismiss={dismissCalendarItemSuccess} itemFilterType={itemFilterType} />
      )}
      {showErrorModal && <DismissErrorNotification onDismiss={() => setErrorModal(false)} />}
      {showRestoreSuccessModal && <RestoreSuccessNotification onDismiss={restoreCalendarItemSuccess} />}
      {!isDismissed && (
        <div css={styles.itemContainer}>
          <Grid container spacing={12}>
            <Grid container item xs={1} justifyContent="center" css={styles.itemIcon}>
              {itemIcon}
            </Grid>
            <Grid container item xs={11} spacing={8}>
              <Grid container item xs={10} css={styles.itemHeaderLine}>
                {itemUrl ? (
                  <Link
                    component={(props: TrackingProps) => <TrackedA {...props} data={additionalTrackData} />}
                    trackingName={trackingName}
                    href={itemUrl}
                    variant="quiet"
                    typographyVariant="subtitleMedium"
                    css={styles.itemHeaderText}
                    data-e2e={`${itemTypeName} link ${itemName}`}
                    target={openLinkInNewTab ? '_blank' : undefined}
                    rel={openLinkInNewTab ? 'noopener noreferrer' : undefined}
                  >
                    {headerContents}
                  </Link>
                ) : (
                  <Typography2 component="h3" variant="subtitleMedium">
                    {headerContents}
                  </Typography2>
                )}

                {statusPill && <span css={styles.itemPillContainer}>{statusPill}</span>}
              </Grid>
              {isDismissible && (
                <Grid container item xs={2} justifyContent="flex-end" css={styles.itemIcon}>
                  {/* Update this button when the new CDS IconButton is released. */}
                  <Button
                    variant="ghost"
                    size="small"
                    disabled={showModal}
                    icon={<CloseIcon color="interactive" size="small" />}
                    onClick={() => setWarningModal(true)}
                    css={styles.dismissButton}
                    aria-label={_t('Dismiss #{itemTypeName}. #{itemName}. #{itemStatus}', {
                      itemName,
                      itemTypeName,
                      itemStatus: itemStatus ?? '',
                    })}
                    children=""
                  />
                </Grid>
              )}
              {isRestorable && (
                <Grid item xs={2} justifyContent="flex-end">
                  <RestoreButton
                    courseId={courseId}
                    itemId={itemId}
                    onClick={() => setRestoreSuccessModal(true)}
                    disabled={showRestoreSuccessModal}
                    onError={hideOverdueItemError}
                  />
                </Grid>
              )}
              <Grid item>
                <div css={styles.itemSubText}>{subHeading}</div>
              </Grid>
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};
